import { useState, useMemo } from 'react'
import { API } from 'aws-amplify'
import { Box, Paper, SvgIcon, IconButton, CircularProgress, Dialog, DialogContent, DialogActions, DialogTitle, DialogContentText, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGridPremium, GridColDef, useGridApiRef, useGridApiContext, GridRowModesModel, GridRowModes, GridRowParams, GridRowModel, GridRenderEditCellParams, GridEventListener, GridRowEditStopReasons, GridCallbackDetails } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../common/Components/PageHeader';
import { Status, useFetch } from '../../hooks/useFetch';
import { useParams } from 'react-router-dom';
import { DestinationGroupFields, AirlineFields } from '@aviation/catering-masterdata-sdk';
import { DynamoDbObject } from '@aviation/catering-common';
import StyledBox from '../../common/Components/StyledBox'
import DestinationCodesInput from '../../common/Components/DestinationCodesInput';
import { ReactComponent as TrashIcon } from '../../icons/trash.svg';
import { ReactComponent as CrossIcon } from '../../icons/cross.svg';
import { ReactComponent as CheckmarkIcon } from '../../icons/checkmark.svg';
import { ulid } from 'ulidx';
import {toast} from "react-toastify";
import DestinationGroupCreateDialog from './DestinationGroupCreateDialog';

function DestinationCodesEditCell(props : GridRenderEditCellParams) {
    const apiRef = useGridApiContext();

    const handleChange = (codes : string[]) => {
        apiRef.current.setEditCellValue({ id: props.id, field: props.field, value: codes });
    };

    return (
        <Box
            sx={{ px: 1, width: '100%' }}
            onKeyDown={(e) => {
                // Enter here should only commit a destination code chip, not save the whole row.
                if(e.key === 'Enter')
                    e.stopPropagation();
            }}
        >
            <DestinationCodesInput value={(props.value as string[]) ?? []} onChange={handleChange} label="" autoFocus tagsMaxHeight={72} />
        </Box>
    );
}

function DestinationGroups() {
    const apiRef = useGridApiRef();
    const { t } = useTranslation();
    const { clientCode } = useParams();
    const { status, data = [] } = useFetch<Array<DestinationGroupFields & DynamoDbObject>>(`/api/masterdata/destinationgroup/${clientCode}`);

    /* eslint-disable @typescript-eslint/no-unused-vars */
    const { data: airlineData = [] } = useFetch<Array<AirlineFields & DynamoDbObject>>(`/api/masterdata/airline/${clientCode}`);

    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [deleteItem, setDeleteItem] = useState<DestinationGroupFields & DynamoDbObject | undefined>(undefined);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [loading, setLoading] = useState(false);

    const rowId = (row : any) : string => {
        return `${row.PK}#${row.RK}`;
    }

    const rows = useMemo(() => {
        return data.map((o) => { return {...o, id: rowId(o), airline: o.RK.split('#')[0] }}).sort((a,b) => (a.Name ?? '') < (b.Name ?? '') ? -1 : 1);
    }, [data]);

    const airlineList = useMemo(() => {
        return airlineData.map(o => { return {label: (o as any).Description ?? '', value: o.RK } }).sort((a,b) => a.label < b.label ? -1 : 1);
    }, [airlineData])

    const columns: GridColDef[] = [
        { field: 'airline', headerName: 'Airline', minWidth: 150, editable: false,
            type: 'singleSelect', valueOptions: airlineList },
        { field: 'Name', headerName: 'Name', minWidth: 200, editable: true },
        { field: 'Destinations', headerName: 'Destinations', minWidth: 300,
            renderCell: (params) => ((params.value as string[]) ?? []).join(', '),
            renderEditCell: (params) => <DestinationCodesEditCell {...params} />,
            editable: true },
        { field: 'actions', type: 'actions', headerName: '', minWidth: 55, renderCell: ({ row }: Partial<GridRowParams>) => {
            const isInEditMode = (rowModesModel[rowId(row)]?.mode ?? GridRowModes.View) === GridRowModes.Edit;
            const isLoading = row.IsLoading ?? false;

            if(isInEditMode)
                return [
                    <IconButton color="success" onClick={handleSaveClick(row)}>
                        <SvgIcon component={CheckmarkIcon} inheritViewBox />
                    </IconButton>,
                    <IconButton color="error" onClick={handleCancelClick(rowId(row))}>
                        <SvgIcon component={CrossIcon} inheritViewBox />
                    </IconButton>
                ]
            else if(!isLoading)
                return (
                    <IconButton color="error" onClick={() => confirmDeletion(row)}>
                        <SvgIcon component={TrashIcon} inheritViewBox />
                    </IconButton>
                )
            else if(isLoading)
                return (
                    <CircularProgress size={19} />
                )
            }
        }
    ];

    const handleRowModesModelChange = (model: GridRowModesModel, details: GridCallbackDetails) => {
        setRowModesModel(model);
      };

    const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
        if(params.reason === GridRowEditStopReasons.rowFocusOut)
            event.defaultMuiPrevented = true;
        else
            setRowModesModel({...rowModesModel, [params.id]: {mode: GridRowModes.View}});

    };

    const processRowUpdate = (row: GridRowModel) => {
        const updatedRow = { ...row, isNew: false, IsLoading: true };

        updateItem(row as DestinationGroupFields & DynamoDbObject).then(success => {
            apiRef.current.updateRows([{ id: rowId(row), IsLoading: false }]);

            if(success)
                toast.success(`Changes to destination group saved successfully.`);
            else
                toast.error(`An error occurred while saving destination group`);
        })
        return updatedRow;
    };

    const handleSaveClick = (item : any) => () => {
        setRowModesModel({...rowModesModel, [rowId(item) ?? '']: {mode: GridRowModes.View }});
    };

    const handleCancelClick = (id : any) => () => {
        setRowModesModel({
          ...rowModesModel,
          [id]: {mode: GridRowModes.View, ignoreModifications: true},
        });
      };

    const handleCloseDialog = () => {
        setDeleteItem(undefined);
        setShowDeleteConfirmation(false);
    };

    const handleDeleteItem = async () => {
        setShowDeleteConfirmation(false);

        if(deleteItem !== undefined) {
            apiRef.current.updateRows([{ id: rowId(deleteItem), IsLoading: true }]);

            const init = {
                body: {},
                headers: {}
            };

            try {
                const keys = deleteItem.RK.split('#');
                await API.del('api', `/api/masterdata/destinationgroup/${clientCode}/${keys[0]}/${keys[1]}`, init);
                apiRef.current.updateRows([{ id: rowId(deleteItem), _action: 'delete' }]);
                toast.success(`Destination group deleted successfully.`);
                return true;
            } catch(e) {
                console.error(e);
                apiRef.current.updateRows([{ id: rowId(deleteItem), IsLoading: false }]);
                toast.error(`An error occurred while deleting a destination group`);
                return false;
            } finally {
                setDeleteItem(undefined);
            }
        }
    };

    const updateItem = async(item : DestinationGroupFields & DynamoDbObject) => {
        const init = {
            body: item,
            headers: {}
        };

        try {
            const keys = item.RK.split('#');
            await API.put('api', `/api/masterdata/destinationgroup/${clientCode}/${keys[0]}/${keys[1]}`, init);
            return true;
        } catch(e) {
            console.error(e);
            return false;
        }
    }

    const confirmDeletion = (item : any) => {
        setDeleteItem(item);
        setShowDeleteConfirmation(true);
    };

    const onCreateItem = async (airline : string, name : string, destinations : string[]) => {
        setShowCreateDialog(false);
        setLoading(true);

        const item : DestinationGroupFields & DynamoDbObject = {
            Name: name,
            Destinations: destinations,
            PK: `${clientCode}#DestinationGroup`,
            RK: `${airline}#${ulid().toString()}`,
        };

        // Define id and airline for grid usage
        (item as any).id = rowId(item);
        (item as any).airline = airline;

        if(await updateItem(item)) {
            setLoading(false);
            apiRef.current.updateRows([item]);
            toast.success(`Destination Group added successfully`);
        } else {
            setLoading(false);
            toast.error(`An error occurred while creating destination group`);
        }
    }

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <PageHeader title={t('Destination Groups')}>
                    <Button variant="contained" onClick={() => setShowCreateDialog(true)}>Create</Button>
                </PageHeader>
            </Grid>
            <Grid item xs={12}>
                <Paper sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: '100px' }} elevation={3}>
                    <StyledBox>
                        <DataGridPremium
                            apiRef={apiRef}
                            autoHeight
                            rows={rows}
                            columns={columns}

                            editMode='row'
                            getRowHeight={(params) => (rowModesModel[params.id]?.mode === GridRowModes.Edit ? 120 : null)}
                            getRowClassName={(params) => { return params.indexRelativeToCurrentPage % 2 === 1 ? `tui-grid-alternate-row` : ''}}
                            pagination
                            loading={status === Status.Fetching || status === Status.Idle || loading}
                            isCellEditable={(params) => true }

                            rowModesModel={rowModesModel}
                            onRowModesModelChange={(m, d) => handleRowModesModelChange(m, d)}
                            onRowEditStop={handleRowEditStop}
                            processRowUpdate={processRowUpdate}
                        />
                    </StyledBox>
                    <DestinationGroupCreateDialog airlineList={airlineList} open={showCreateDialog} cancelAction={() => setShowCreateDialog(false)} createAction={onCreateItem} />
                    <Dialog open={showDeleteConfirmation} onClose={handleCloseDialog}>
                        <DialogTitle id="alert-dialog-title">
                            {"Delete Destination Group?"}
                        </DialogTitle>
                        <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Do you want to delete this destination group? You can't undo this action.
                        </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog} autoFocus>Cancel</Button>
                            <Button onClick={handleDeleteItem} color="error">Delete</Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            </Grid>
        </Grid>
    );
}

export default DestinationGroups;
